import { CliError, CliErrorCode } from '@wix/cli-error';
import { isType } from 'variant';
import { runCommand } from '../shell/index.js';
import type { IPackageManager } from '../types.js';
import { RepoType } from '../repo-type.js';

export class NPM implements IPackageManager {
  readonly name = 'npm';

  constructor(private readonly repoType: RepoType) {}

  getUsage(name: string): string {
    return `npm init ${name.replace('create-', '')}`;
  }

  getInstallCmd(): string {
    return 'npm install';
  }

  getRunCmd(): string {
    return 'npm run';
  }

  getRunBinaryCmd(args: string[]): { file: string; args: string[] } {
    return {
      file: 'npx',
      args: ['--no-install'].concat(args),
    };
  }

  async setup(_cwd: string): Promise<void> {
    if (isType(this.repoType, RepoType.Monorepo)) {
      throw new CliError({
        code: CliErrorCode.UnsupportedPackageManagerWorkspaces({
          packageManagerName: this.name,
        }),
        cause: null,
      });
    }
  }

  async runInstall(cwd: string): Promise<void> {
    try {
      await runCommand(this.getInstallCmd(), { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallPackages(),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }

  async runInstallPackage(cwd: string, packageName: string): Promise<void> {
    try {
      await runCommand(`npm install ${packageName}`, { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallPackage({ packageName }),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }

  async runUninstallPackage(cwd: string, packageName: string): Promise<void> {
    try {
      await runCommand(`npm uninstall ${packageName}`, { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUninstallPackage({ packageName }),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }
}
